// Landed-cost allocation — spec §9.3. Allocates a total cost across receipt
// lines proportional to line value, folding the integer-cent remainder onto the
// LARGEST-value line (never the positionally-last line — Simplyfi hit a real
// negative-cost bug doing that; see allocation.test.ts regression case).
import { CoreError } from './errors.js';
import { largestRemainderAllocate } from './internal/largest-remainder.js';
export function allocateLandedCost(totalCents, lines) {
    if (lines.length === 0) {
        if (totalCents !== 0n) {
            throw new CoreError('ALLOCATION_NO_LINES', 'Cannot allocate a non-zero amount across zero lines');
        }
        return [];
    }
    const seen = new Set();
    for (const line of lines) {
        if (seen.has(line.id)) {
            throw new CoreError('ALLOCATION_DUPLICATE_ID', `Duplicate line id in allocation input: ${line.id}`);
        }
        seen.add(line.id);
    }
    // Allocate on the absolute value so the largest-remainder math (which assumes
    // non-negative operands) is well-defined; re-apply the sign at the end. Landed
    // costs are practically always positive, but this keeps the identity
    // sum(allocations) === totalCents exact regardless.
    const sign = totalCents < 0n ? -1n : 1n;
    const absTotal = totalCents < 0n ? -totalCents : totalCents;
    const totalValue = lines.reduce((sum, l) => sum + (l.valueCents > 0n ? l.valueCents : 0n), 0n);
    const items = totalValue > 0n
        ? lines.map((l) => ({ id: l.id, weight: l.valueCents > 0n ? l.valueCents : 0n, tieBreak: l.valueCents }))
        : lines.map((l) => ({ id: l.id, weight: 1n, tieBreak: l.valueCents }));
    const allocated = largestRemainderAllocate(absTotal, items);
    const result = lines.map((l) => ({ id: l.id, cents: (allocated.get(l.id) ?? 0n) * sign }));
    const sum = result.reduce((s, r) => s + r.cents, 0n);
    if (sum !== totalCents) {
        // Should be unreachable given the largest-remainder identity above; guards against
        // a future refactor silently breaking conservation.
        throw new CoreError('ALLOCATION_INVARIANT_VIOLATED', 'Allocation did not sum to totalCents');
    }
    if (totalCents >= 0n) {
        for (const r of result) {
            if (r.cents < 0n) {
                throw new CoreError('ALLOCATION_NEGATIVE', `Allocation produced a negative amount for line ${r.id}`);
            }
        }
    }
    return result;
}
//# sourceMappingURL=allocation.js.map